import type {
  ActionSettings,
  DelaySettings,
  MarketingFlowStep,
  TriggerSettings
} from '@/views/autopilot/types/marketingFlow'

import useLocalStorage from '@/composables/useLocalStorage'
import useFrequencyType from './frequencyType'

import { TEMPLATES_CONFIG } from '@/views/autopilot/config/templates.config'

const sharedState = {
  isUsingTemplate: ref<boolean>(false)
}

export const useMarketingFlowTemplates = () => {
  const { t } = useI18n()
  const { getSegmentIdFromFrequencyTypeSegments } = useFrequencyType()
  const { stores } = useLocalStorage()

  const isUsingTemplate = sharedState.isUsingTemplate

  const defaultTriggerSettings: TriggerSettings = {
    triggerType: null,
    selectedAudience: null,
    selectedRewards: [],
    cooldownMinutes: null,
    selectedStores: [],
    includedSegments: [],
    excludedSegments: [],
    maxTriggersPerUser: 1,
    checkinCount: null,
    customCheckinCount: null,
    multipleTimeScenario: false
  }
  const defaultDelaySettings: DelaySettings = {
    daysBeforeAction: null,
    sendImmediatlyAfterTrigger: true,
    sendAtSpecificHour: false,
    sendAfterDelay: false,
    delayUnit: 'DAYS',
    hourToSend: null,
    hourToSendLabel: null
  }
  const defaultActionSettings: ActionSettings = {
    actions: [],
    conversionDelay: 7,
    isTransactional: false
  }

  const triggerSettings = ref<TriggerSettings>({ ...defaultTriggerSettings })
  const delaySettings = ref<DelaySettings>({ ...defaultDelaySettings })
  const actionSettings = ref<ActionSettings>({ ...defaultActionSettings })
  const marketingFlowName = ref<string>(null)
  const currentStep = ref<MarketingFlowStep>('trigger')

  const resetTemplate = () => {
    isUsingTemplate.value = false

    triggerSettings.value = { ...defaultTriggerSettings }
    delaySettings.value = { ...defaultDelaySettings }
    actionSettings.value = { ...defaultActionSettings }
  }

  const setTemplate = async (templateType: string, audienceId?: number) => {
    const template = TEMPLATES_CONFIG.find((t) => t.type === templateType)
    if (!template) return

    isUsingTemplate.value = true

    // Deep clone template settings to prevent mutating the original config
    const clonedTriggerSettings = structuredClone(template.triggerSettings) as Partial<TriggerSettings>

    // Start from default settings, then apply cloned template settings, then runtime values
    triggerSettings.value = {
      ...defaultTriggerSettings,
      ...clonedTriggerSettings,
      selectedStores: stores.value.map((store) => store.id)
    }

    if (audienceId) {
      triggerSettings.value.selectedAudience = audienceId
    } else if (template.requiresSegmentType) {
      triggerSettings.value.selectedAudience = getSegmentIdFromFrequencyTypeSegments(template.requiresSegmentType)
    }

    delaySettings.value = {
      ...defaultDelaySettings,
      ...template.delaySettings
    }

    actionSettings.value = {
      ...defaultActionSettings,
      isTransactional: template.actionSettings.isTransactional,
      actions:
        template.actionSettings.actions?.map((action) => {
          if (action.actionType === 'SEND_SMS' && template.messageContentKey) {
            return {
              ...action,
              messageContent: template.messageContentVariables
                ? t(template.messageContentKey, template.messageContentVariables)
                : t(template.messageContentKey)
            }
          }
          return action
        }) || []
    }

    currentStep.value = 'trigger'
    marketingFlowName.value = t(template.title)
  }

  return {
    actionSettings,
    currentStep,
    delaySettings,
    isUsingTemplate,
    marketingFlowName,
    resetTemplate,
    setTemplate,
    triggerSettings
  }
}
